import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

interface MessageNotification {
  id: string;
  senderName: string;
  senderAvatar?: string;
  message: string;
  chatId: string;
}

export const useMessageNotifications = () => {
  const { user } = useAuth();
  const [notification, setNotification] = useState<MessageNotification | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    
    try {
      // Get all user's chats
      const { data: chats } = await supabase
        .from("community_chats")
        .select("id")
        .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`);

      if (!chats?.length) {
        setUnreadCount(0);
        return;
      }

      // Count unread messages across all chats
      const chatIds = chats.map(c => c.id);
      const { count } = await supabase
        .from("chat_direct_messages")
        .select("*", { count: "exact", head: true })
        .in("chat_id", chatIds)
        .eq("is_read", false)
        .neq("sender_id", user.id);

      setUnreadCount(count || 0);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    fetchUnreadCount();

    // Subscribe to new messages
    const channel = supabase
      .channel('message-notifications')
      .on(
        'postgres_changes',
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'chat_direct_messages',
          filter: `sender_id=neq.${user.id}`
        },
        async (payload) => {
          const newMessage = payload.new as any;
          
          // Check if this message is for a chat the user is part of
          const { data: chat } = await supabase
            .from("community_chats")
            .select("id, user1_id, user2_id")
            .eq("id", newMessage.chat_id)
            .single();

          if (chat && (chat.user1_id === user.id || chat.user2_id === user.id)) {
            // Fetch sender profile
            const { data: sender } = await supabase
              .from("profiles")
              .select("display_name, username, avatar_url")
              .eq("id", newMessage.sender_id)
              .single();

            if (sender) {
              setNotification({
                id: newMessage.id,
                senderName: sender.display_name || sender.username || "User",
                senderAvatar: sender.avatar_url || undefined,
                message: newMessage.content,
                chatId: newMessage.chat_id
              });
            }

            // Update unread count
            fetchUnreadCount();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchUnreadCount]);

  const dismissNotification = useCallback(() => {
    setNotification(null);
  }, []);

  return {
    notification,
    dismissNotification,
    unreadCount,
    refetchUnreadCount: fetchUnreadCount
  };
};

export default useMessageNotifications;
